// ============================================================================
// MissingFeatures ViewModel
// ============================================================================

import { useState, useCallback, useRef, useMemo } from "react";
import type { MissingFeaturesProps, MissingFeaturesViewModel } from "./MissingFeatures.types";

/**
 * ViewModel hook for MissingFeatures component
 */
export function useMissingFeaturesViewModel(props: MissingFeaturesProps): MissingFeaturesViewModel {
	const { issues, currentIssueIndex, skippedIssueIds, onDeleteJob, onUploadPoints, onUploadZones } = props;

	// Visibility state
	const [hideBoundary, setHideBoundary] = useState(false);

	// Upload state
	const [isUploadingPoints, setIsUploadingPoints] = useState(false);
	const [isUploadingZones, setIsUploadingZones] = useState(false);
	const pointsFileInputRef = useRef<HTMLInputElement>(null);
	const zonesFileInputRef = useRef<HTMLInputElement>(null);

	// Derived state
	const hasIssues = issues.length > 0;
	const currentIssue = hasIssues ? (issues[currentIssueIndex] ?? null) : null;
	const isSkipped = currentIssue ? skippedIssueIds.has(currentIssue.id) : false;

	// Job display name (grower/field)
	const jobDisplayName = useMemo(() => {
		if (!currentIssue) return "";
		const job = currentIssue.job;
		const grower = job.growerName || job.importGrowerName || "Unknown Grower";
		const field = job.fieldName || job.importFieldName || "Unknown Field";
		return `${grower} / ${field}`;
	}, [currentIssue]);

	// Toggle boundary visibility
	const toggleBoundaryVisibility = useCallback(() => {
		setHideBoundary((prev) => !prev);
	}, []);

	// Handle delete job
	const handleDeleteJob = useCallback(() => {
		if (currentIssue) {
			onDeleteJob(currentIssue.job.id);
		}
	}, [currentIssue, onDeleteJob]);

	// Upload availability
	const canUploadPoints = !!onUploadPoints;
	const canUploadZones = !!onUploadZones;

	// Points upload handlers
	const handleUploadPointsClick = useCallback(() => {
		pointsFileInputRef.current?.click();
	}, []);

	const handlePointsFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const files = event.target.files;
			if (!files || files.length === 0 || !currentIssue || !onUploadPoints) return;

			setIsUploadingPoints(true);
			try {
				await onUploadPoints(currentIssue.job.id, Array.from(files));
			} finally {
				setIsUploadingPoints(false);
				if (pointsFileInputRef.current) {
					pointsFileInputRef.current.value = "";
				}
			}
		},
		[currentIssue, onUploadPoints],
	);

	// Zones upload handlers
	const handleUploadZonesClick = useCallback(() => {
		zonesFileInputRef.current?.click();
	}, []);

	const handleZonesFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const files = event.target.files;
			if (!files || files.length === 0 || !currentIssue || !onUploadZones) return;

			setIsUploadingZones(true);
			try {
				await onUploadZones(currentIssue.job.id, Array.from(files));
			} finally {
				setIsUploadingZones(false);
				if (zonesFileInputRef.current) {
					zonesFileInputRef.current.value = "";
				}
			}
		},
		[currentIssue, onUploadZones],
	);

	return {
		hasIssues,
		currentIssue,
		isSkipped,
		hideBoundary,
		toggleBoundaryVisibility,
		handleDeleteJob,
		jobDisplayName,
		isUploadingPoints,
		pointsFileInputRef,
		handleUploadPointsClick,
		handlePointsFileChange,
		canUploadPoints,
		isUploadingZones,
		zonesFileInputRef,
		handleUploadZonesClick,
		handleZonesFileChange,
		canUploadZones,
	};
}
